/**
 * Resolves the base URLs for HTTP API + WebSocket connections.
 *
 * Three runtime environments to handle:
 *   1. Web (browser, prod):    qualquer host → https://api.my360.site
 *   2. Web (browser, dev):     localhost:63601 → relativo `/api`, que o
 *      Vite faz proxy para a API local em :3000
 *   3. Capacitor (Android/iOS): file://localhost (ou capacitor://) → URL
 *      relativa não alcança servidor nenhum, então precisa da absoluta —
 *      que agora é a mesma de todo mundo, e não mais um caso especial.
 *
 * The Capacitor detection key is `window.Capacitor` injected by the
 * native runtime — present on both Android (file://) and iOS
 * (capacitor://) platforms. NOT present in Vite preview / browser.
 */

/**
 * A API vive em subdomínio próprio.
 *
 * Antes era `/api` na mesma origem do site. Separar tem duas consequências
 * boas: o endereço deixa de depender de onde o front está servido (site,
 * app logado, domínio white-label de cliente), e o cache da Cloudflare na
 * frente do site nunca esbarra em rota dinâmica.
 *
 * Foi seguro fazer porque a autenticação é Bearer token em localStorage, e
 * não cookie — token cross-origin funciona com CORS, cookie exigiria
 * SameSite=None e uma discussão bem maior.
 */
const PROD_API = 'https://api.my360.site';
const PROD_WS_HOST = 'wss://api.my360.site';

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => string;
    };
  }
}

export function isCapacitor(): boolean {
  if (typeof window === 'undefined') return false;
  // isNativePlatform is the canonical check; getPlatform fallback for
  // older Capacitor versions or stripped builds.
  if (typeof window.Capacitor?.isNativePlatform === 'function') {
    return window.Capacitor.isNativePlatform();
  }
  if (typeof window.Capacitor?.getPlatform === 'function') {
    const p = window.Capacitor.getPlatform();
    return p === 'android' || p === 'ios';
  }
  return false;
}

/** Returns the prefix to prepend to API endpoints (`/auth/login` etc). */
/**
 * Nossos hosts. Fora desta lista, o SPA está num domínio de CLIENTE.
 *
 * Domínio white-label é arbitrário — `tour.imobiliaria.com.br`, o que a
 * pessoa comprar — e por isso não pode entrar na lista de origens do CORS.
 * Nesses domínios o SPA fala com `/api` na PRÓPRIA origem, que o Caddy faz
 * proxy para a API: mesma origem, CORS não se aplica, nada a autorizar.
 */
const OWN_HOSTS = new Set([
  'my360.site',
  'www.my360.site',
  'app.my360.site',
  'status.my360.site',
]);

export function getApiBase(): string {
  // Em desenvolvimento continua relativo: o Vite faz o proxy de /api para
  // a API local, e apontar para produção faria o dev mexer em dados reais.
  if (import.meta.env.DEV) return '/api';
  // O app nativo não tem servidor na origem file:// — precisa do absoluto.
  if (isCapacitor()) return PROD_API;
  if (typeof window !== 'undefined' && !OWN_HOSTS.has(window.location.hostname)) {
    return '/api';
  }
  return PROD_API;
}

/** Returns the origin (no trailing slash) for WebSocket connections. */
export function getWsOrigin(): string {
  // Mesma regra do HTTP, pelo mesmo motivo: em domínio de cliente o
  // WebSocket sobe na própria origem, que o Caddy encaminha.
  if (typeof window !== 'undefined') {
    const sameOrigin =
      import.meta.env.DEV || (!isCapacitor() && !OWN_HOSTS.has(window.location.hostname));
    if (sameOrigin) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}`;
    }
  }
  return PROD_WS_HOST;
}

/** Whether the app is running inside the native shell (used to swap
 *  per-platform UI behaviour like default landscape orientation). */
export function isNative(): boolean {
  return isCapacitor();
}
